import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { MdCheckCircle, MdCancel, MdRemoveCircle, MdPerson, MdContentCopy } from "react-icons/lib/md";
import moment from "moment";
import { get } from "utils/api";
import { AudioPlayer } from "components/ui";
import { formatScore } from "./call-quality-utils";

// inverted: true means FALSE is the positive/good result
// agentOnly: true means only show for Agent calls
const QUALITY_QUESTIONS = [
  { key: "twoIdentifiersConfirmed", question: "Confirmed two identifiers for security", inverted: false },
  { key: "callRecordingDisclosed", question: "Stated the call was being recorded", inverted: false },
  { key: "dobIdentified", question: "Identified patient's date of birth", inverted: false },
  { key: "coachIntroLag", question: "Had lag time or hesitation in introduction", inverted: true },
  { key: "cpapResupplyEducation", question: "Educated patient on CPAP resupply schedule", inverted: false },
  { key: "physicianIdentifiedAndVerified", question: "Identified and verified physician", inverted: false },
  { key: "insuranceCarrierIdentifiedAndVerified", question: "Identified and verified insurance carrier", inverted: false },
  { key: "cpapDysfunctionsRelayed", question: "Relayed CPAP dysfunctions", inverted: false },
  { key: "disclaimerReadEntirely", question: "Read disclaimer entirely", inverted: false },
  { key: "shippingAddressVerified", question: "Verified shipping address", inverted: false },
  { key: "shippingAddressStepVerified", question: "Offered/confirmed contact method", inverted: false },
  { key: "productsAndSizesVerified", question: "Verified products and sizes", inverted: false },
  { key: "copayDeductibleOffered", question: "Attempted copay/deductible collection", inverted: false },
  { key: "acceptableVoiceVolume", question: "Communicated with acceptable voice volume", inverted: false },
  { key: "friendlyAttitude", question: "Presented a friendly attitude", inverted: false },
  { key: "patientRefusedAIOrRequestedHuman", question: "Patient refused AI or requested human", inverted: true, agentOnly: true }
];

const getSentimentClass = (sentiment) => {
  if (!sentiment) return "neutral";
  const lower = sentiment.toLowerCase();
  if (lower === "positive") return "positive";
  if (lower === "negative") return "negative";
  return "neutral";
};

const CallDetailsModal = ({ call, onClose }) => {
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [loadingRecording, setLoadingRecording] = useState(false);
  const [recordingError, setRecordingError] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  if (!call) return null;

  const isAgentCall = call.agentType?.toLowerCase() === "agent";
  const callIdentifier = isAgentCall ? call.synthpopUUID : call.callSid;
  const identifierLabel = isAgentCall ? "Synthpop UUID" : "Call SID";

  const handleLoadRecording = async () => {
    if (recordingUrl || !call.recordingSid) return;

    setLoadingRecording(true);
    setRecordingError(false);
    try {
      const response = await get(`callrecordings/${call.recordingSid}/uri`);
      if (response) {
        setRecordingUrl(response);
      } else {
        setRecordingError(true);
      }
    } catch (error) {
      console.error("Failed to fetch recording:", error);
      setRecordingError(true);
    } finally {
      setLoadingRecording(false);
    }
  };

  const handleCopyId = async (id, type) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(type);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Clipboard API may fail in insecure contexts
    }
  };

  // Filter questions based on call type
  const applicableQuestions = QUALITY_QUESTIONS.filter(q => !q.agentOnly || isAgentCall);

  // Categorize questions into good, bad, and not applicable
  const categorizeQuestion = (question) => {
    const value = call[question.key];
    if (value === null || value === undefined) {
      return "na";
    }
    const isGood = question.inverted ? !value : value;
    return isGood ? "good" : "bad";
  };

  const goodQuestions = applicableQuestions.filter(q => categorizeQuestion(q) === "good");
  const badQuestions = applicableQuestions.filter(q => categorizeQuestion(q) === "bad");
  const naQuestions = applicableQuestions.filter(q => categorizeQuestion(q) === "na");

  const renderQuestionItem = (question, category) => {
    const value = call[question.key];
    const actualAnswer = value === true ? "Yes" : value === false ? "No" : "N/A";

    return (
      <div key={question.key} className={`question-item ${category}`}>
        {category === "good" && <MdCheckCircle className="question-icon" />}
        {category === "bad" && <MdCancel className="question-icon" />}
        {category === "na" && <MdRemoveCircle className="question-icon" />}
        <span className="question-text">{question.question}</span>
        <span className="answer-text">{actualAnswer}</span>
      </div>
    );
  };

  return (
    <div className="call-details-modal-overlay" onClick={onClose}>
      <div className="call-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>Call Details</h3>
            <p className="modal-subtitle">Quality breakdown for this call</p>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="call-info-grid">
            <div className="info-item">
              <span className="info-label">Date/Time</span>
              <span className="info-value">{moment(call.callDateTime).format("MMM D, YYYY h:mm A")}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Patient</span>
              <span className="info-value">
                {call.patientGuid ? (
                  <Link to={`/patients/${call.patientGuid}`} className="patient-link" target="_blank">
                    <MdPerson className="link-icon" />
                    {call.patientName || "Unknown"}
                  </Link>
                ) : (
                  call.patientName || "Unknown"
                )}
              </span>
            </div>
            {call.accountNumber && (
              <div className="info-item">
                <span className="info-label">Account #</span>
                <span className="info-value">
                  <code className="account-number">{call.accountNumber}</code>
                  <button
                    type="button"
                    className="copy-btn-inline"
                    onClick={() => handleCopyId(call.accountNumber, "account")}
                    title="Copy account number"
                  >
                    <MdContentCopy />
                    {copiedId === "account" && <span className="copied-text">Copied!</span>}
                  </button>
                </span>
              </div>
            )}
            <div className="info-item">
              <span className="info-label">Agent</span>
              <span className="info-value">
                <span className={`agent-type-badge ${call.agentType?.toLowerCase()}`}>{call.agentType}</span>
                {call.agentName}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Score</span>
              <span className="info-value score-value">{formatScore(call.score, "N/A")}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Disposition</span>
              <span className="info-value">{call.disposition || "N/A"}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Sentiment</span>
              <span className="info-value">
                <span className={`sentiment-badge ${getSentimentClass(call.patientSentiment)}`}>
                  Patient: {call.patientSentiment || "N/A"}
                </span>
                <span className={`sentiment-badge ${getSentimentClass(call.agentSentiment)}`}>
                  Agent: {call.agentSentiment || "N/A"}
                </span>
              </span>
            </div>
          </div>

          <div className="call-identifiers">
            {callIdentifier && (
              <div className="identifier-item">
                <span className="identifier-label">{identifierLabel}:</span>
                <code className="identifier-value">{callIdentifier}</code>
                <button
                  type="button"
                  className="copy-btn"
                  onClick={() => handleCopyId(callIdentifier, "main")}
                  title="Copy to clipboard"
                >
                  <MdContentCopy />
                  {copiedId === "main" && <span className="copied-text">Copied!</span>}
                </button>
              </div>
            )}
            {call.recordingSid && (
              <div className="recording-player">
                {recordingUrl ? (
                  <AudioPlayer src={recordingUrl} showTableFormatted showAllFeatures={false} />
                ) : recordingError ? (
                  <span className="recording-error">Recording unavailable</span>
                ) : (
                  <button
                    type="button"
                    className="recording-btn"
                    onClick={handleLoadRecording}
                    disabled={loadingRecording}
                  >
                    {loadingRecording ? "Loading..." : "Load Recording"}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="questions-grid">
            <div className="questions-column good">
              <div className="column-header">
                <MdCheckCircle className="header-icon" />
                <span>Passed ({goodQuestions.length})</span>
              </div>
              <div className="column-content">
                {goodQuestions.length > 0 ? (
                  goodQuestions.map(q => renderQuestionItem(q, "good"))
                ) : (
                  <div className="empty-column">No items</div>
                )}
              </div>
            </div>

            <div className="questions-column bad">
              <div className="column-header">
                <MdCancel className="header-icon" />
                <span>Failed ({badQuestions.length})</span>
              </div>
              <div className="column-content">
                {badQuestions.length > 0 ? (
                  badQuestions.map(q => renderQuestionItem(q, "bad"))
                ) : (
                  <div className="empty-column">No items</div>
                )}
              </div>
            </div>

            <div className="questions-column na">
              <div className="column-header">
                <MdRemoveCircle className="header-icon" />
                <span>Not Applicable ({naQuestions.length})</span>
              </div>
              <div className="column-content">
                {naQuestions.length > 0 ? (
                  naQuestions.map(q => renderQuestionItem(q, "na"))
                ) : (
                  <div className="empty-column">No items</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CallDetailsModal.propTypes = {
  call: PropTypes.shape({
    callDateTime: PropTypes.string,
    patientGuid: PropTypes.string,
    patientName: PropTypes.string,
    accountNumber: PropTypes.string,
    agentName: PropTypes.string,
    agentType: PropTypes.string,
    score: PropTypes.number,
    disposition: PropTypes.string,
    patientSentiment: PropTypes.string,
    agentSentiment: PropTypes.string,
    contactGuid: PropTypes.string,
    recordingSid: PropTypes.string,
    synthpopUUID: PropTypes.string,
    callSid: PropTypes.string,
    twoIdentifiersConfirmed: PropTypes.bool,
    callRecordingDisclosed: PropTypes.bool,
    dobIdentified: PropTypes.bool,
    coachIntroLag: PropTypes.bool,
    cpapResupplyEducation: PropTypes.bool,
    physicianIdentifiedAndVerified: PropTypes.bool,
    insuranceCarrierIdentifiedAndVerified: PropTypes.bool,
    cpapDysfunctionsRelayed: PropTypes.bool,
    disclaimerReadEntirely: PropTypes.bool,
    shippingAddressVerified: PropTypes.bool,
    shippingAddressStepVerified: PropTypes.bool,
    productsAndSizesVerified: PropTypes.bool,
    copayDeductibleOffered: PropTypes.bool,
    acceptableVoiceVolume: PropTypes.bool,
    friendlyAttitude: PropTypes.bool,
    patientRefusedAIOrRequestedHuman: PropTypes.bool
  }),
  onClose: PropTypes.func.isRequired
};

export default CallDetailsModal;
